/**
 * Client helper for the `send-document-email` Edge Function.
 *
 * Used by ShareModal and any other code that wants to email a document to
 * one or more recipients. The Edge Function does the Resend send; this
 * just calls it via supabase.functions.invoke so the auth header is
 * attached automatically.
 *
 * IMPORTANT: the caller is responsible for generating the signed URL
 * (typically via getShareUrl / getSignedUrl in lib/storage.ts). This
 * helper doesn't know how to talk to Supabase Storage — it just delivers
 * whatever URL it's handed.
 */

import { supabase } from './supabase';

export type DocumentType =
  | 'RAMS'
  | 'Accident report'
  | 'Subbie induction'
  | 'Permit'
  | 'Toolbox talk'
  | 'CPP'
  | 'Document'
  | 'Site sign-in log';

export interface SendDocumentEmailArgs {
  /** One or more recipient email addresses. */
  to: string[];
  /** Human-readable document title — appears in subject and email body. */
  docTitle: string;
  /** Document category — drives subject line and body copy. */
  docType: DocumentType;
  /** Signed URL the recipient will download from. Must be absolute http(s). */
  docUrl: string;
  /** Optional — sender org name (shown in email body). */
  orgName?: string;
  /** Optional — site name (shown in email body). */
  siteName?: string;
  /** Optional — name of the user sending (shown in email body). */
  senderName?: string;
  /** Optional — free-text message from the sender (shown in a callout box). */
  note?: string;
  /** Optional — ISO timestamp the share link expires (defaults to "30 days"). */
  expiresAt?: string;
}

export interface SendDocumentEmailResult {
  sent: boolean;
  id?: string;          // Resend message id on success
  recipients?: number;  // how many addresses Resend accepted
  error?: string;
}

/**
 * Sends the document email via the Edge Function. Soft-fails — never throws.
 * Callers should display result.error if !result.sent.
 */
export async function sendDocumentEmail(args: SendDocumentEmailArgs): Promise<SendDocumentEmailResult> {
  if (!args.to || args.to.length === 0) {
    return { sent: false, error: 'No recipients given.' };
  }
  if (args.to.length > 10) {
    return { sent: false, error: 'Too many recipients — limit 10 per send.' };
  }
  if (!args.docUrl || !/^https?:\/\//.test(args.docUrl)) {
    return { sent: false, error: 'Bad share link.' };
  }

  try {
    const { data, error } = await supabase.functions.invoke('send-document-email', {
      body: {
        to:         args.to,
        docTitle:   args.docTitle,
        docType:    args.docType,
        docUrl:     args.docUrl,
        orgName:    args.orgName,
        siteName:   args.siteName,
        senderName: args.senderName,
        note:       args.note,
        expiresAt:  args.expiresAt,
      },
    });
    if (error) {
      return { sent: false, error: error.message || 'Send failed.' };
    }
    const result = data as { sent?: boolean; id?: string; recipients?: number; error?: string } | null;
    if (result?.error) {
      return { sent: false, error: result.error };
    }
    return {
      sent: !!result?.sent,
      id: result?.id,
      recipients: result?.recipients,
    };
  } catch (e) {
    return { sent: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Lightweight email-list parser. Splits on commas, semicolons, or
 * whitespace, trims, dedupes, drops empties. Doesn't validate the
 * format strictly — that happens server-side and in the Edge Function.
 */
export function parseEmailList(input: string): string[] {
  const parts = input
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set(parts));
}
